import type { NextAuthOptions } from "next-auth";
import { JWT } from "next-auth/jwt";
import CredentialsProvider from "next-auth/providers/credentials";
import { IdTokenResponse } from "../../../../../types/types";
import { logError } from "@lib/logger";
import { GLOBALS } from "config";
import { comiciPlusClient } from "apiClient/ComiciPlusClient";

const COMICI_AUTH_ENDPOINT = GLOBALS.AUTH_ENDPOINT;

export const AUTH_TOKEN_REFRESH_ERROR = "RefreshAccessTokenError";

function parseJwt(token: string) {
  const base64Url = token.split(".")[1];
  if (!base64Url) {
    return null;
  }
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  return JSON.parse(Buffer.from(base64, "base64").toString());
}

async function refreshAccessToken(token: JWT) {
  try {
    const response = await fetch(COMICI_AUTH_ENDPOINT + "/auth/tokens", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        idToken: token.idToken,
        refreshToken: token.refreshToken,
      }),
    });

    const idToken: IdTokenResponse = await response.json();
    if (!idToken) {
      return {
        ...token,
        error: AUTH_TOKEN_REFRESH_ERROR,
      };
    }

    return {
      ...token,
      idToken: idToken.idToken,
      refreshToken: idToken.refreshToken ?? token.refreshToken, // Fall back to old refresh token
    };
  } catch (error) {
    return {
      ...token,
      error: "RefreshAccessTokenError",
    };
  }
}

export const authOptions: NextAuthOptions = {
  pages: {
    signIn: "/auth/signin",
  },
  providers: [
    CredentialsProvider({
      id: "credentials",
      name: "ID&pass",
      credentials: {
        id: { label: "ユーザー名", type: "text", placeholder: "例: comici" },
        password: { label: "パスワード", type: "password" },
      },
      /**
       * 認証を行い、Userオブジェクトを返す
       */
      async authorize(credentials, req) {
        try {
          const res = await comiciPlusClient.userAction.postNormalAuth({
            userAgent: req.headers ? req.headers["user-agent"] || "" : "",
            normalAuthRequestModel: {
              identifier: credentials?.id || "",
              password: credentials?.password || "",
            },
          });

          const idToken: IdTokenResponse = res as IdTokenResponse;
          if (idToken) {
            // ユーザー名と有効期限をIDトークンから取得
            const obj = parseJwt(idToken.idToken);
            if (obj) {
              return {
                id: obj.userId,
                user: obj.userId,
                expiresIn: obj.exp,
                idToken: idToken.idToken,
                refreshToken: idToken.refreshToken,
                loginMissions: res.comebackLoginMissions,
              };
            }
          }
        } catch (err) {
          if (err instanceof Error) {
            logError("auth error. id:" + credentials?.id);
          } else {
            console.error(err);
          }
        }
        return null;
      },
    }),
    CredentialsProvider({
      id: "social",
      name: "social login",
      credentials: {
        idToken: { type: "text" },
        refreshToken: { type: "text" },
        loginMissions: { type: "text" },
        //mailAddress: { type: "text" },
        //comiciId: { type: "text" },
      },
      /**
       * 認証を行い、Userオブジェクトを返す
       */
      authorize(credentials) {
        // ユーザー名と有効期限をIDトークンから取得
        if (credentials?.idToken) {
          const obj = parseJwt(credentials.idToken);
          return {
            id: obj.userId,
            user: obj.userId,
            expiresIn: obj.exp,
            idToken: credentials.idToken,
            refreshToken: credentials?.refreshToken,
            loginMissions: JSON.parse(credentials.loginMissions).missions,
          };
        }
        return null;
      },
    }),
  ],
  secret: process.env.NEXTAUTH_SECRET,
  callbacks: {
    jwt: async ({ token, user, trigger, session }) => {
      // New login
      if (user) {
        return {
          ...user,
        };
      }

      // 期限切れ-5分までは現在のトークンを返す
      if (token.expiresIn && Date.now() < (token.expiresIn - 60 * 5) * 1000) {
        if (trigger == "update" && session?.user) {
          token.user = session.user;
          token.id = session.user;
        }
        return token;
      }

      // 期限切れ間近の場合はトークンをリフレッシュ
      return refreshAccessToken(token);
    },

    session: ({ session, token }) => {
      session.user = token.user;
      session.idToken = token.idToken;
      session.refreshToken = token.refreshToken;
      session.error = token.error;
      session.loginMissions = token.loginMissions;
      token.loginMissions = undefined;
      return session;
    },
  },
};
